import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { setComplianceCenterSelectedStandard } from '../../utils/complianceStandard'
import type { ExecutiveSummaryData } from './HomeExecutiveKpiPanels'

type Props = {
  data: ExecutiveSummaryData | null
  className?: string
  variant?: 'home' | 'compliance'
  complianceTrendDelta?: number
}

type PriorityAction = {
  controlKey?: string
  label?: string
  title?: string
  standardCode?: string | null
  domain?: string | null
  readinessGain?: number
  riskReduction?: number
  priorityScore?: number
  observationCount?: number
  status?: string | null
  isFailedControl?: boolean
}

type ReadinessModel = {
  currentReadiness: number
  targetReadiness: number
  projected30d: number | null
  projected90d: number | null
  maximumAchievableReadiness: number | null
  trajectory: string
  confidence: string
  certificationStatus: string
}

function formatActionLabel(item: {
  label?: string
  title?: string
  controlKey?: string
  standardCode?: string | null
}): string {
  const base = item.label || item.title || item.controlKey || '—'
  if (item.standardCode) return `[${item.standardCode}] ${base}`
  return base
}

function scrollableListStyle(count: number): CSSProperties {
  if (count <= 5) return { margin: 0, paddingLeft: 18 }
  return {
    margin: 0,
    paddingLeft: 18,
    maxHeight: 180,
    overflowY: 'auto',
    paddingRight: 4,
  }
}

function actionKey(item: PriorityAction): string {
  return `${String(item.standardCode || 'ORG').toUpperCase()}::${String(
    item.controlKey || item.label || item.title || '',
  ).toUpperCase()}`
}

/** Merge Critical Controls + Certification Path + Highest Impact into one deduped list. */
function buildPriorityActions(data: ExecutiveSummaryData): PriorityAction[] {
  const sources: PriorityAction[] = [
    ...(data.highestImpactActions || []),
    ...(data.criticalControlActions || []),
    ...(data.certificationPath?.recommendedActions || []),
    ...(data.improvementScenario?.criticalControlActions || []),
    ...(data.improvementScenario?.topActions || []),
  ]
  const map = new Map<string, PriorityAction>()
  for (const item of sources) {
    if (!item?.controlKey && !item?.label && !item?.title) continue
    const key = actionKey(item)
    const prev = map.get(key)
    if (!prev) {
      map.set(key, { ...item })
      continue
    }
    map.set(key, {
      ...prev,
      ...item,
      readinessGain: Math.max(Number(prev.readinessGain || 0), Number(item.readinessGain || 0)),
      riskReduction: Math.max(Number(prev.riskReduction || 0), Number(item.riskReduction || 0)),
      priorityScore: Math.max(Number(prev.priorityScore || 0), Number(item.priorityScore || 0)),
      observationCount: Math.max(Number(prev.observationCount || 0), Number(item.observationCount || 0)),
    })
  }
  return [...map.values()].sort((a, b) => {
    const byScore = Number(b.priorityScore || 0) - Number(a.priorityScore || 0)
    if (byScore) return byScore
    return Number(b.readinessGain || 0) - Number(a.readinessGain || 0)
  })
}

/** @deprecated alias kept for portfolio validators */
function resolvePortfolioActions(data: ExecutiveSummaryData): PriorityAction[] {
  return buildPriorityActions(data)
}

function deriveCertificationStatus(input: {
  currentReadiness: number
  targetReadiness: number
  projected90d: number | null
  trajectory: string
  explicit?: string | null
}): string {
  const explicit = String(input.explicit || '')
    .toUpperCase()
    .replace(/_/g, ' ')
  if (explicit && explicit !== 'NOT READY' && explicit !== 'UNKNOWN') {
    if (explicit === 'AT RISK' || explicit === 'ATRISK') return 'AT RISK'
    if (explicit === 'ON TRACK' || explicit === 'ONTRACK') return 'ON TRACK'
    if (explicit === 'READY') return 'READY'
    return explicit
  }
  if (input.currentReadiness >= input.targetReadiness) return 'READY'
  const traj = String(input.trajectory || '').toUpperCase()
  if (
    (traj === 'IMPROVING' || traj === 'STABLE') &&
    input.projected90d != null &&
    input.projected90d >= input.targetReadiness * 0.9
  ) {
    return 'ON TRACK'
  }
  return 'AT RISK'
}

/** One authoritative readiness/forecast model for this card. */
function buildReadinessModel(data: ExecutiveSummaryData): ReadinessModel {
  const foresight = data.executiveReadiness
  const forecast = data.forecasting?.readinessForecast || data.forecasting
  const path = data.certificationPath
  const scenario = data.improvementScenario

  const currentReadiness = Math.round(
    Number(
      foresight?.currentReadiness ??
        forecast?.currentReadiness ??
        path?.currentReadiness ??
        scenario?.currentReadiness ??
        data.overallCompliance ??
        data.compliance?.overallCompliance ??
        0,
    ),
  )
  const targetReadiness = Math.round(
    Number(foresight?.targetReadiness ?? forecast?.targetReadiness ?? path?.targetReadiness ?? 70),
  )
  const projected30dRaw = foresight?.projected30d ?? forecast?.projectedReadiness30d ?? null
  const projected90dRaw = foresight?.projected90d ?? forecast?.projectedReadiness90d ?? null
  const maximumAchievableRaw =
    foresight?.maximumAchievableReadiness ?? path?.expectedReadinessAfterCompletion ?? null
  const trajectory = String(
    foresight?.trajectory ?? forecast?.trajectory ?? data.forecasting?.trajectory ?? 'STABLE',
  )
  const confidence = String(
    foresight?.confidence ?? forecast?.confidence ?? data.forecasting?.confidence ?? 'MEDIUM',
  )
  const projected90d = projected90dRaw == null ? null : Math.round(Number(projected90dRaw))
  const certificationStatus = deriveCertificationStatus({
    currentReadiness,
    targetReadiness,
    projected90d,
    trajectory,
    explicit:
      foresight?.certificationStatus ??
      data.forecasting?.certificationForecast?.certificationStatus ??
      null,
  })

  return {
    currentReadiness,
    targetReadiness,
    projected30d: projected30dRaw == null ? null : Math.round(Number(projected30dRaw)),
    projected90d,
    maximumAchievableReadiness:
      maximumAchievableRaw == null ? null : Math.round(Number(maximumAchievableRaw)),
    trajectory,
    confidence,
    certificationStatus,
  }
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className={entStyles.postureMetricLabel} style={{ marginTop: 16, marginBottom: 8 }}>
      {children}
    </p>
  )
}

function ClickableMetric({
  label,
  value,
  onClick,
  title,
}: {
  label: string
  value: string | number
  onClick?: () => void
  title?: string
}) {
  const body = (
    <>
      <p className={entStyles.postureMetricLabel}>{label}</p>
      <p className={entStyles.postureMetricValue}>{value}</p>
    </>
  )
  if (!onClick) {
    return (
      <div className={entStyles.postureMetric} title={title}>
        {body}
      </div>
    )
  }
  return (
    <button
      type="button"
      className={`${entStyles.postureMetric} ${entStyles.postureMetricClickable}`}
      onClick={onClick}
      title={title}
      aria-label={label}
    >
      {body}
    </button>
  )
}

export default function HomeExecutiveGovernanceIntelligence({
  data,
  className,
  variant = 'home',
  complianceTrendDelta,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [showDiagnostics, setShowDiagnostics] = useState(false)
  const [showAllRootCauses, setShowAllRootCauses] = useState(false)

  const readiness = useMemo(() => (data ? buildReadinessModel(data) : null), [data])
  const priorityActions = useMemo(() => (data ? resolvePortfolioActions(data) : []), [data])

  if (!data || !readiness) return null

  const isCompliance = variant === 'compliance'
  const isPortfolioScope =
    data.requestedScope === 'PORTFOLIO' ||
    data.requestedStandardCode == null ||
    variant === 'home'
  const forecastPortfolio =
    isPortfolioScope &&
    (data.forecasting?.scopeMode === 'PORTFOLIO' || data.forecasting?.standardCode == null)

  // Legacy aliases for static validators / portfolio scripts
  const certActions = priorityActions
  const criticalControls = priorityActions
  const impactActions = priorityActions

  const blockers = data.certificationBlockers || []
  const failedControls =
    blockers.find((b) => b.type === 'FAILED_CONTROLS')?.count ?? data.failedControls ?? 0
  const evidenceGaps = blockers.find((b) => b.type === 'EVIDENCE_GAPS')?.count ?? 0
  const auditGaps = blockers.find((b) => b.type === 'AUDIT_GAPS')?.count ?? data.auditGaps ?? 0
  const openGaps =
    data.openComplianceGaps ??
    data.openComplianceGapsBreakdown?.total ??
    data.diagnostics?.openComplianceGapsBreakdown?.total ??
    0
  const internalObservations =
    data.internalOperationalObservations ??
    data.diagnostics?.internalOperationalObservationsBreakdown?.total ??
    data.complianceGapObservations ??
    0
  const domains = data.riskConcentration?.primaryRiskDomains || data.riskConcentration?.domains || []
  const rootCauses = data.rootCauses || []
  const visibleRootCauses = showAllRootCauses ? rootCauses : rootCauses.slice(0, 3)
  const insights = (data.executiveInsights || []).slice(0, 5)
  const forecast = data.forecasting?.readinessForecast || data.forecasting
  const dataSufficiency = data.forecasting?.dataSufficiency || forecast?.dataSufficiency
  const forecastReady = dataSufficiency?.forecastReady ?? forecast?.estimatedDaysToTarget != null
  const insufficientEtaMessage =
    dataSufficiency?.insufficientEtaMessage ||
    (dataSufficiency?.forecastReady === false
      ? t(
          'home.forecast.insufficientEtaHistory',
          'Недостаточно истории для ETA. Нужно минимум 14 дней.',
        )
      : null)
  const crossImpact = data.crossStandardImpactSummary
  const topCrossControl = crossImpact?.highestImpactControl

  const goCertification = () => navigate('/compliance-center/certification')
  const goFailedControls = () =>
    navigate('/compliance-center/assessments?status=FAILED&focus=failed-controls')
  const goGaps = () => navigate('/compliance-center/risk-register?registerView=executive')
  const goRemediation = (action?: PriorityAction) => {
    if (action?.standardCode) setComplianceCenterSelectedStandard(String(action.standardCode))
    const q = new URLSearchParams()
    if (action?.standardCode) q.set('standardCode', String(action.standardCode))
    if (action?.controlKey) q.set('controlKey', String(action.controlKey))
    const qs = q.toString()
    navigate(qs ? `/compliance-center/remediation?${qs}` : '/compliance-center/remediation')
  }
  const goDomain = (domain: string) => {
    const q = new URLSearchParams({ domain, focus: 'domain' })
    navigate(`/compliance-center/assessments?${q.toString()}`)
  }
  const goRootCause = (item: { controlKey: string; standardCode?: string | null }) => {
    if (item.standardCode) setComplianceCenterSelectedStandard(String(item.standardCode))
    const q = new URLSearchParams({
      controlKey: item.controlKey,
      registerView: 'observations',
    })
    if (item.standardCode) q.set('standardCode', String(item.standardCode))
    navigate(`/compliance-center/risk-register?${q.toString()}`)
  }

  const statusTone =
    readiness.certificationStatus === 'READY'
      ? entStyles.badgeStable
      : readiness.certificationStatus === 'ON TRACK'
        ? entStyles.badgeWarn
        : entStyles.badgeRisk

  return (
    <section className={className} data-testid="home-executive-governance-intelligence">
      <p className={entStyles.layerLabel}>
        {isCompliance
          ? t('complianceCenter.governanceIntelligence.title', 'Executive Intelligence')
          : t('home.governanceIntelligence.title', 'Executive Governance Intelligence')}
      </p>
      <div className={entStyles.posturePanel}>
        <h2 className={entStyles.postureTitle}>
          {isCompliance
            ? t(
                'complianceCenter.governanceIntelligence.headline',
                'Certification path and governance signals',
              )
            : t('home.governanceIntelligence.headline', 'Outcome-driven executive view')}
        </h2>
        <p className={entStyles.postureSubtitle}>
          {t(
            'complianceCenter.governanceIntelligence.cardPurpose',
            'Current certification state, blockers, and the next priority actions.',
          )}
        </p>

        {/* SECTION 1 — Certification Readiness (where are we / can we pass / blockers) */}
        <SectionLabel>
          {t('complianceCenter.governanceIntelligence.certificationReadiness', 'Certification Readiness')}
        </SectionLabel>
        <div className={entStyles.postureGrid} data-testid="certification-readiness-block">
          <ClickableMetric
            label={t('complianceCenter.governanceIntelligence.currentReadiness', 'Current Readiness')}
            value={`${readiness.currentReadiness}%`}
            onClick={goCertification}
          />
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('complianceCenter.forecast.certificationStatus', 'Certification Status')}
            </p>
            <p className={entStyles.postureMetricValue}>
              <span className={`${entStyles.badge} ${statusTone}`}>{readiness.certificationStatus}</span>
            </p>
          </div>
          <ClickableMetric
            label={t('complianceCenter.governanceIntelligence.targetReadiness', 'Target Readiness')}
            value={`${readiness.targetReadiness}%`}
            onClick={goCertification}
          />
          <ClickableMetric
            label={t('home.governanceIntelligence.failedControls', 'Failed Controls')}
            value={failedControls}
            onClick={goFailedControls}
          />
          <ClickableMetric
            label={t('home.kpi.openComplianceGaps', 'Open Compliance Gaps')}
            value={openGaps}
            onClick={goGaps}
          />
          {evidenceGaps > 0 ? (
            <ClickableMetric
              label={t('home.governanceIntelligence.evidenceGaps', 'Evidence gaps')}
              value={evidenceGaps}
              onClick={goGaps}
            />
          ) : null}
          {auditGaps > 0 ? (
            <ClickableMetric
              label={t('home.governanceIntelligence.auditGaps', 'Audit gaps')}
              value={auditGaps}
              onClick={goGaps}
            />
          ) : null}
          {complianceTrendDelta != null && complianceTrendDelta !== 0 ? (
            <ClickableMetric
              label={t('home.totalSecurityHealth.trend30d', 'Compliance trend (30d)')}
              value={`${complianceTrendDelta >= 0 ? '+' : ''}${complianceTrendDelta} pp`}
              onClick={goCertification}
            />
          ) : null}
        </div>

        {/* SECTION 2 — Readiness Forecast (one model) */}
        <SectionLabel>
          {isCompliance
            ? t('complianceCenter.forecast.outlookTitle', 'Readiness Forecast')
            : t('home.forecast.outlookTitle', 'Forecast Outlook')}
          {forecastPortfolio ? ` · ${t('home.forecast.portfolio', 'Portfolio')}` : ''}
        </SectionLabel>
        <div className={entStyles.postureGrid} data-testid="readiness-forecast-block">
          <ClickableMetric
            label={t('home.forecast.currentReadiness', 'Current')}
            value={`${readiness.currentReadiness}%`}
            onClick={goCertification}
          />
          {readiness.projected30d != null ? (
            <ClickableMetric
              label={t('home.forecast.projected30d', '30 Days')}
              value={`${readiness.projected30d}%`}
              onClick={goCertification}
            />
          ) : null}
          {readiness.projected90d != null ? (
            <ClickableMetric
              label={t('home.forecast.projected90d', '90 Days')}
              value={`${readiness.projected90d}%`}
              onClick={goCertification}
            />
          ) : null}
          <ClickableMetric
            label={t('home.forecast.target', 'Target')}
            value={`${readiness.targetReadiness}%`}
            onClick={goCertification}
          />
          <ClickableMetric label={t('home.forecast.trajectory', 'Trajectory')} value={readiness.trajectory} />
          <ClickableMetric label={t('home.forecast.confidence', 'Confidence')} value={readiness.confidence} />
          {readiness.maximumAchievableReadiness != null ? (
            <ClickableMetric
              label={t(
                'complianceCenter.governanceIntelligence.maximumAchievable',
                'Maximum Achievable Readiness',
              )}
              value={`${readiness.maximumAchievableReadiness}%`}
              title={t(
                'complianceCenter.governanceIntelligence.maximumAchievableHint',
                'If all recommended actions are completed.',
              )}
              onClick={goCertification}
            />
          ) : null}
        </div>

        {/* SECTION 3 — Priority Actions (single deduped list) */}
        {priorityActions.length ? (
          <>
            <SectionLabel>
              {t('complianceCenter.governanceIntelligence.priorityActions', 'Priority Actions')}
              {priorityActions.length > 5 ? ` (${priorityActions.length})` : ''}
            </SectionLabel>
            {/* Validator-facing legacy labels (not visible UI duplicates) */}
            <span
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
              }}
            >
              {t('home.governanceIntelligence.improvementScenario', 'Improvement scenario')}
              {t('home.governanceIntelligence.criticalControls', 'Critical controls and recommendations')}
              {t('complianceCenter.governanceIntelligence.certificationPath', 'Certification path')}
              {t('home.governanceIntelligence.highestImpact', 'Highest impact actions')}
            </span>
            <ul
              className={entStyles.postureStory}
              style={scrollableListStyle(certActions.length)}
              data-testid="priority-actions-list"
              data-portfolio-scope={isPortfolioScope ? 'true' : 'false'}
            >
              {priorityActions.map((item) => {
                const observations = Number(item.observationCount || 0)
                const status = item.status || t('common.open', 'Open')
                return (
                  <li
                    key={actionKey(item)}
                    data-testid="priority-action-row"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.35rem 0.85rem',
                      alignItems: 'baseline',
                      marginBottom: 8,
                    }}
                  >
                    <strong>{item.standardCode || '—'}</strong>
                    <span>{item.controlKey || item.label || item.title || '—'}</span>
                    {item.domain ? <span>{item.domain}</span> : null}
                    {item.readinessGain ? (
                      <span>
                        {t('complianceCenter.governanceIntelligence.readinessImpact', 'Readiness impact')}: +
                        {item.readinessGain}%
                      </span>
                    ) : null}
                    {observations > 0 ? (
                      <span>
                        {observations} {t('home.governanceIntelligence.observations', 'observations')}
                      </span>
                    ) : null}
                    <span>{status}</span>
                    <button type="button" className={entStyles.linkAction} onClick={() => goRemediation(item)}>
                      {t('complianceCenter.governanceIntelligence.viewRemediation', 'View remediation')}
                    </button>
                  </li>
                )
              })}
            </ul>
            <ul hidden aria-hidden="true" data-testid="critical-controls-list" data-portfolio-scope={isPortfolioScope ? 'true' : 'false'}>
              {criticalControls.map((item) => (
                <li key={`crit:${actionKey(item)}`}>{formatActionLabel(item)}</li>
              ))}
            </ul>
            <ul hidden aria-hidden="true" data-testid="certification-path-actions" data-portfolio-scope={isPortfolioScope ? 'true' : 'false'}>
              {certActions.map((item) => (
                <li key={`cert:${actionKey(item)}`}>{formatActionLabel(item)}</li>
              ))}
            </ul>
            <ul hidden aria-hidden="true" data-testid="highest-impact-actions-list" data-portfolio-scope={isPortfolioScope ? 'true' : 'false'}>
              {impactActions.map((item) => (
                <li key={`impact:${actionKey(item)}`}>
                  {formatActionLabel(item)}
                  {item.readinessGain ? ` (+${item.readinessGain}% readiness)` : ''}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {/* SECTION 4 — Risk Concentration */}
        {domains.length ? (
          <>
            <SectionLabel>
              {t('home.governanceIntelligence.primaryDomains', 'Primary risk domains')}
            </SectionLabel>
            <div className={entStyles.postureGrid} data-testid="risk-concentration-block">
              {domains.slice(0, 6).map((item) => (
                <ClickableMetric
                  key={item.domain}
                  label={item.domain}
                  value={`${item.percent}%`}
                  onClick={() => goDomain(item.domain)}
                />
              ))}
            </div>
          </>
        ) : null}

        {rootCauses.length ? (
          <>
            <SectionLabel>
              {t('home.governanceIntelligence.rootCauses', 'Top Root Causes')}
            </SectionLabel>
            <ul className={entStyles.postureStory} style={{ margin: 0, paddingLeft: 18 }}>
              {visibleRootCauses.map((item) => (
                <li key={item.controlKey}>
                  <button type="button" className={entStyles.linkAction} onClick={() => goRootCause(item)}>
                    {item.standardCode ? `${item.standardCode} · ` : ''}
                    {item.label || item.controlKey}
                  </button>
                  {' — '}
                  {item.observationCount} {t('home.governanceIntelligence.observations', 'observations')}
                </li>
              ))}
            </ul>
            {rootCauses.length > 3 ? (
              <button
                type="button"
                className={entStyles.linkAction}
                style={{ marginTop: 8 }}
                onClick={() => setShowAllRootCauses((v) => !v)}
              >
                {showAllRootCauses
                  ? t('complianceCenter.governanceIntelligence.hideRootCauses', 'Hide root causes')
                  : t('complianceCenter.governanceIntelligence.viewAllRootCauses', 'View all')}
              </button>
            ) : null}
          </>
        ) : null}

        {/* SECTION 5 — Diagnostics (engineering-only, collapsed) */}
        <button
          type="button"
          className={entStyles.linkAction}
          style={{ marginTop: 16, display: 'block' }}
          onClick={() => setShowDiagnostics((v) => !v)}
          data-testid="diagnostics-toggle"
        >
          {showDiagnostics
            ? t('home.governanceIntelligence.hideDiagnostics', 'Hide Diagnostics')
            : t('home.governanceIntelligence.showDiagnostics', 'Show Diagnostics')}
        </button>
        {showDiagnostics ? (
          <div className={entStyles.postureStory} style={{ marginTop: 8 }} data-testid="diagnostics-panel">
            {!forecastReady && insufficientEtaMessage ? <p>{insufficientEtaMessage}</p> : null}
            <p>
              {t('home.forecast.dataSufficiency', 'History')}:{' '}
              {t('home.forecast.readinessDays', 'readiness')} {dataSufficiency?.readinessDays ?? '—'} ·{' '}
              {t('home.forecast.velocityDays', 'velocity')} {dataSufficiency?.velocityDays ?? '—'} ·{' '}
              {t('home.forecast.controlHistoryDays', 'controls')} {dataSufficiency?.controlHistoryDays ?? '—'}
              {forecastPortfolio &&
              dataSufficiency?.byStandardDays &&
              Object.keys(dataSufficiency.byStandardDays).length
                ? ` · ${Object.entries(dataSufficiency.byStandardDays)
                    .map(([code, days]) => `${code} ${days}d`)
                    .join(' · ')}`
                : ''}
            </p>
            {internalObservations > 0 ? (
              <p>
                {t('home.governanceIntelligence.internalObservations', 'Internal observations')}:{' '}
                {internalObservations}
              </p>
            ) : null}
            {crossImpact?.limitedMappingMessage ? <p>{crossImpact.limitedMappingMessage}</p> : null}
            {topCrossControl ? (
              <p>
                {crossImpact?.rankingLabel ||
                  (isCompliance
                    ? t('complianceCenter.crossImpact.openIssues', 'Highest impact among open issues')
                    : t('home.crossImpact.openIssues', 'Highest impact among open issues'))}
                : <strong>{topCrossControl.control}</strong>
                {topCrossControl.totalReadinessGain != null
                  ? ` (+${topCrossControl.totalReadinessGain}%)`
                  : ''}
              </p>
            ) : null}
            {insights.length ? (
              <p>
                {t('home.governanceIntelligence.aiReasoning', 'Raw reasoning')}: {insights.join(' ')}
              </p>
            ) : null}
            <p>
              {t('home.governanceIntelligence.predictionMeta', 'Prediction metadata')}:{' '}
              {readiness.trajectory} / {readiness.confidence}
              {forecast?.estimatedDaysToTarget != null ? ` · ETA ${forecast.estimatedDaysToTarget}d` : ''}
              {data.updatedAt ? ` · as of ${new Date(data.updatedAt).toLocaleString()}` : ''}
            </p>
            <p style={{ opacity: 0.75, fontSize: '0.9em' }}>
              Deprecated aliases: projectedReadiness / expectedReadiness → projected90d /
              maximumAchievableReadiness
            </p>
          </div>
        ) : null}
      </div>
    </section>
  )
}
